import { Box, Button, Icon, Typography } from "@outschool/backpack";
import { faStar } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { ErrorMessage } from "@outschool/ui-components-shared";
import {
  PublicReviewQuote,
  PublicReviewsOfLeaderQueryOptions,
  StarRatings,
  usePublicReviewsOfLeaderQuery
} from "@outschool/ui-components-website";
import { Carousel, Loading } from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import React from "react";

import * as Env from "../../../shared/Env";
import { ReviewRespondButton } from "./ReviewRespondButton";

type PublicReviewsOfLeaderProps = {
  leaderUid: string;
  handleOpenReviewModal(): void;
};

export const PublicReviewsOfLeader: React.FunctionComponent<
  PublicReviewsOfLeaderProps
> = ({ leaderUid, handleOpenReviewModal }) => {
  const { t } = useTranslation(
    "client\\components\\reviews\\PublicReviewsOfLeader"
  );
  const isMobile = Screen.useIsMobile();
  const isTablet = Screen.useIsTablet();

  const options: PublicReviewsOfLeaderQueryOptions = {
    skip: !leaderUid,
    variables: {
      leaderUid,
      offset: 0,
      limit: 4,
      skipAggregates: false,
      includeSummary: false
    }
  };

  const { data, loading, error } = usePublicReviewsOfLeaderQuery(options);

  const reviewsHeading = (
    <Typography
      variant="h3"
      sx={{
        marginBottom: "0.5em"
      }}
    >
      {t("Reviews")}
    </Typography>
  );

  if (error) {
    return (
      <Box
        sx={{
          paddingLeft: isMobile ? "24px" : "0px"
        }}
      >
        {reviewsHeading}
        <ErrorMessage
          value={error}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
      </Box>
    );
  }

  if (loading || !data) {
    return (
      <Box
        sx={{
          paddingLeft: isMobile ? "24px" : "0px"
        }}
      >
        {reviewsHeading}
        <Loading />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          paddingLeft: isMobile ? "24px" : "0px"
        }}
      >
        {reviewsHeading}
        <Loading />
      </Box>
    );
  }

  const {
    // @ts-ignore TS(2339): Property 'recentReviews' does not exist on type '{... Remove this comment to see the full error message
    recentReviews,
    // @ts-ignore TS(2339): Property 'reviewsWithTextCount' does not exist on ... Remove this comment to see the full error message
    reviewsWithTextCount,
    // @ts-ignore TS(2339): Property 'averageActivityStarRating' does not exis... Remove this comment to see the full error message
    averageActivityStarRating,
    // @ts-ignore TS(2339): Property 'reviewCount' does not exist on type '{ _... Remove this comment to see the full error message
    reviewCount
    // @ts-ignore TS(2531): Object is possibly 'null'.
  } = data.publicProfile.leader;

  if (recentReviews.length === 0) {
    return (
      <Box
        sx={{
          paddingLeft: isMobile ? "24px" : "0px"
        }}
      >
        {reviewsHeading}
        <Box flex>
          <Icon
            icon={faStar}
            sx={{
              fontSize: "23px"
            }}
          />
          <Typography
            variant="inherit"
            sx={{
              maxWidth: "320px",
              marginLeft: "1em",
              marginRight: "2em"
            }}
          >
            <Trans t={t}>This teacher has no public reviews (yet).</Trans>
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          paddingLeft: isMobile ? "24px" : "0px"
        }}
      >
        {reviewsHeading}

        <Box
          sx={{
            marginBottom: "0.5em"
          }}
        >
          <StarRatings
            rating={averageActivityStarRating}
            sx={{ marginRight: "0.5em" }}
          />
          {t("{{reviewCount}} total review", {
            reviewCount,
            count: reviewCount,
            defaultValue_plural: "{{reviewCount}} total reviews"
          })}
        </Box>

        {reviewsWithTextCount > recentReviews.length ? (
          <Button
            size="medium"
            sx={{
              marginBottom: "0.5em"
            }}
            onClick={handleOpenReviewModal}
          >
            <Typography
              variant="inherit"
              sx={{
                fontWeight: "fontWeightRegular"
              }}
            >
              {
                /** Note there must be more than one review so not using plural syntax */
                t("Show All {{reviewCount}} Reviews", { reviewCount })
              }
            </Typography>
          </Button>
        ) : null}
      </Box>

      {isMobile ? (
        <Carousel>
          {recentReviews.map((review: $TSFixMe, idx: $TSFixMe) => (
            <PublicReviewQuote
              containerSx={{
                width: "71vw",
                marginRight: "12px",
                marginTop: "0.5em",
                paddingLeft: idx === 0 ? "24px" : "0px" // we want reviews to bleed to the left edge on horizontal scroll, but we also want the first review to have left padding
              }}
              reviewSx={{ minHeight: "230px" }}
              showMoreMaxChars={150}
              // @ts-ignore TS(2531): Object is possibly 'null'.
              teacher={data.publicProfile.leader}
              activity={review.activity}
              review={review}
              key={review.uid + "/quote"}
              trackingLabel="public_review_of_leader"
              ReviewRespondButton={ReviewRespondButton}
            />
          ))}
        </Carousel>
      ) : (
        <Box
          flex
          sx={{
            flexWrap: "wrap",
            width: "100%",
            flexDirection: isTablet ? "column" : "row"
          }}
        >
          {recentReviews.map((review: $TSFixMe, idx: $TSFixMe) => (
            <PublicReviewQuote
              containerSx={{
                paddingY: "0.5em",
                paddingRight: idx % 2 === 0 ? "0.5em" : "0px",
                paddingLeft: idx % 2 !== 0 ? "0.5em" : "0px",
                width: isTablet ? "100%" : "50%"
              }}
              reviewSx={{ minHeight: "237px" }}
              showMoreMaxChars={200}
              // @ts-ignore TS(2531): Object is possibly 'null'.
              teacher={data.publicProfile.leader}
              activity={review.activity}
              review={review}
              key={review.uid + "/quote"}
              trackingLabel="public_review_of_leader"
              ReviewRespondButton={ReviewRespondButton}
            />
          ))}
        </Box>
      )}
    </React.Fragment>
  );
};
